import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import NewsCard from '@/components/NewsCard';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, FileX } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const partyFilters = [
  { value: '', label: 'All Parties' },
  { value: 'labour', label: 'Labour' },
  { value: 'conservative', label: 'Conservative' },
  { value: 'green', label: 'Green' }
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [party, setParty] = useState(searchParams.get('party') || '');
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    if (query || party) {
      performSearch();
    }
  }, [searchParams]);

  const performSearch = async () => {
    setLoading(true);
    try {
      const params = {};
      const q = searchParams.get('q');
      const p = searchParams.get('party');
      
      if (q) params.search = q;
      if (p) params.party = p;

      const [articlesRes, countRes] = await Promise.all([
        axios.get(`${API}/articles`, { params }),
        axios.get(`${API}/articles/count`, { params })
      ]);

      setArticles(articlesRes.data);
      setTotalCount(countRes.data.count);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (party) params.set('party', party);
    setSearchParams(params);
  };

  const handlePartyFilter = (value) => {
    setParty(value);
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set('party', value);
    } else {
      params.delete('party');
    }
    setSearchParams(params);
  };

  return (
    <div className="min-h-screen" data-testid="search-page">
      {/* Search Header */}
      <section className="py-12 border-b border-slate-200 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-serif text-3xl md:text-4xl font-medium mb-6">Search Archive</h1>
          
          <form onSubmit={handleSearch} className="flex gap-3 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search articles, politicians, regions..."
                className="pl-10 rounded-none border-slate-300 h-12"
                data-testid="search-query-input"
              />
            </div>
            <Button type="submit" className="btn-primary h-12" data-testid="search-button">
              Search
            </Button>
          </form>

          {/* Party Filters */}
          <div className="flex flex-wrap gap-2">
            {partyFilters.map(filter => (
              <button
                key={filter.value}
                onClick={() => handlePartyFilter(filter.value)}
                className={`party-filter ${party === filter.value ? 'active' : ''}`}
                data-testid={`search-filter-${filter.value || 'all'}`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Results Count */}
          {(query || party) && (
            <p className="text-sm text-slate-500 mb-6 font-mono" data-testid="results-count">
              {totalCount} result{totalCount !== 1 ? 's' : ''} found
              {query && <span> for "{query}"</span>}
              {party && <span> in {party}</span>}
            </p>
          )}

          {loading ? (
            <div className="bento-grid">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="news-card">
                  <div className="skeleton h-4 w-20 mb-4"></div>
                  <div className="skeleton h-6 w-full mb-2"></div>
                  <div className="skeleton h-6 w-3/4 mb-4"></div>
                  <div className="skeleton h-16 w-full"></div>
                </div>
              ))}
            </div>
          ) : articles.length === 0 ? (
            <div className="py-24 text-center">
              <FileX className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <h3 className="font-serif text-xl mb-2">No results found</h3>
              <p className="text-slate-500 text-sm">
                Try adjusting your search terms or filters.
              </p>
            </div>
          ) : (
            <div className="bento-grid stagger-children">
              {articles.map((article, index) => (
                <NewsCard 
                  key={article.id} 
                  article={article} 
                  featured={index === 0 && articles.length > 3}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
